import { useState } from "react";
import moment from "moment";
import Input from "components/Input";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import cruService from "api/cruService";
import { ENotificationType, notification } from "utils/notification";
import { getErrorMessage } from "utils/getErrorMessage";
import styles from "./styles.module.scss";

// Formularz podłączenia / podmiany klucza integracji z Centralnym Rejestrem Umów (MF) —
// port grupy „Centralny Rejestr Umów (CRU)" z Kwantum.Administration/Settings/View
// (CruSettingsViewModel: NewApiKey → SaveCommand). Klucz API wydaje MF dla jednostki
// i jest ważny 6 miesięcy − 1 dzień; REGON jednostki jest pierwszą stroną każdej
// publikowanej umowy (Workspaces nie ma kolumny Regon, więc trzymany przy kluczu).
// Używany w katalogu „Dodaj integrację" (IntegrationMenu) i w „Aktualizuj klucz"
// na stronie ustawień CRU (CruSettings).

interface Props {
  toggle: () => void;
  onSuccess: () => void;
  /** Tryb podmiany klucza (integracja już istnieje). */
  isUpdate?: boolean;
  initialRegon?: string | null;
}

const ConfigureCru = ({ toggle, onSuccess, isUpdate = false, initialRegon }: Props) => {
  const { mutate: setApiKey, isLoading } = cruService.useSetApiKey();
  const { data: settings } = cruService.useGetSettings();

  const [apiKey, setKey] = useState("");
  const [regon, setRegon] = useState(initialRegon ?? "");
  const [error, setError] = useState<string | null>(null);

  const regonOk = regon === "" || regon.length === 9 || regon.length === 14;
  const isComplete = apiKey.trim().length > 0 && regonOk && (isUpdate || regon.length > 0);

  const save = () => {
    setError(null);
    setApiKey(
      { ApiKey: apiKey.trim(), InstitutionRegon: regon || null },
      {
        onSuccess: (s) => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Centralny Rejestr Umów",
            text: `Zapisano klucz API CRU (ważny do ${moment(s.KeyValidTo).format("DD.MM.YYYY")}).`,
          });
          onSuccess();
        },
        onError: (e) => setError(getErrorMessage(e) || "Nie udało się zapisać klucza API."),
      },
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        {isUpdate ? "Aktualizacja klucza API CRU" : "Centralny Rejestr Umów (CRU)"}
      </div>
      <div className={styles.body}>
        Klucz API wydaje Ministerstwo Finansów dla jednostki sektora finansów publicznych w
        portalu CRU; jest ważny 6 miesięcy. Publikacji podlegają umowy zawarte od{" "}
        {settings ? moment(settings.PublicationStart).format("DD.MM.YYYY") : "01.07.2026"}.
        {settings?.Environment === "Test" && " Środowisko: testowe MF."}
      </div>

      <div className={styles.form}>
        <Input
          value={apiKey}
          type="password"
          label="Klucz API CRU"
          placeholder="Wklej klucz API z portalu CRU"
          onChange={setKey}
          autoComplete="new-password"
        />
        <Input
          value={regon}
          label="REGON jednostki (9 lub 14 cyfr)"
          placeholder="REGON"
          onChange={(v: string) => setRegon(v.replace(/\D/g, "").slice(0, 14))}
          isError={regonOk ? undefined : "REGON ma 9 albo 14 cyfr"}
        />
        <div className={styles.hint}>
          REGON jednostki jest doklejany automatycznie jako pierwsza strona (JSFP) każdej
          umowy publikowanej w CRU.
        </div>
        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.buttonsWrapper}>
          <HollowButton text="Anuluj" onClick={toggle} />
          <Button
            text="Zapisz klucz"
            onClick={save}
            loading={isLoading}
            disabled={isLoading || !isComplete}
          />
        </div>
      </div>
    </div>
  );
};

export default ConfigureCru;
